/**
 * Le fichier clients du studio : qui vient, combien de fois, combien il a
 * dépensé depuis le début.
 *
 * Les compteurs sont tenus par le backend au moment du paiement — ils sont en
 * lecture seule ici.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Typography, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, CircularProgress, Alert, Tooltip, Card, CardContent,
  InputAdornment, Avatar, Divider, List, ListItem, ListItemText,
} from '@mui/material';
import {
  Edit, Delete, Add, Search, History, People, Phone, Badge as BadgeIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { clientStudioAPI, extraireListe } from '../../../services/studioAPI';

const FORMULAIRE_VIDE = { nom: '', telephone: '', cin_nif: '', notes: '' };

const htg = (valeur) => `${Number(valeur || 0).toLocaleString()} HTG`;

const dateCourte = (valeur) => {
  if (!valeur) return '—';
  const date = new Date(valeur);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [dialogOuvert, setDialogOuvert] = useState(false);
  const [enEdition, setEnEdition] = useState(false);
  const [formulaire, setFormulaire] = useState(FORMULAIRE_VIDE);
  const [historique, setHistorique] = useState(null);
  const [historiqueLoading, setHistoriqueLoading] = useState(false);

  const charger = async () => {
    try {
      setLoading(true);
      setErreur(null);
      const reponse = await clientStudioAPI.getAll();
      setClients(extraireListe(reponse));
    } catch (error) {
      const message = error.response?.data?.detail || error.message
        || 'Erreur lors du chargement des clients';
      setErreur(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { charger(); }, []);

  // Filtre local : la liste tient dans une page, inutile d'interroger le serveur
  const affiches = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    if (!terme) return clients;
    return clients.filter((client) =>
      [client.nom, client.telephone, client.cin_nif]
        .filter(Boolean).join(' ').toLowerCase().includes(terme)
    );
  }, [clients, recherche]);

  const totalDepense = clients.reduce((somme, c) => somme + Number(c.total_depense || 0), 0);
  const totalVisites = clients.reduce((somme, c) => somme + (c.nombre_visites || 0), 0);

  const ouvrirDialog = (client = null) => {
    if (client) {
      setFormulaire({
        id: client.id,
        nom: client.nom,
        telephone: client.telephone || '',
        cin_nif: client.cin_nif || '',
        notes: client.notes || '',
      });
      setEnEdition(true);
    } else {
      setFormulaire(FORMULAIRE_VIDE);
      setEnEdition(false);
    }
    setDialogOuvert(true);
  };

  const enregistrer = async () => {
    if (!formulaire.nom.trim()) return toast.error('Le nom est obligatoire');
    try {
      if (enEdition) {
        // PATCH : les compteurs sont read_only, inutile de renvoyer la fiche entière
        await clientStudioAPI.patch(formulaire.id, formulaire);
        toast.success('Fiche modifiée');
      } else {
        await clientStudioAPI.create(formulaire);
        toast.success('Fiche créée');
      }
      setDialogOuvert(false);
      charger();
    } catch (error) {
      toast.error(
        error.response?.data?.nom?.[0] || error.response?.data?.detail
        || "Erreur lors de l'enregistrement"
      );
    }
  };

  const supprimer = async (client) => {
    if (!window.confirm(`Supprimer la fiche de ${client.nom} ?`)) return;
    try {
      await clientStudioAPI.delete(client.id);
      toast.success('Fiche supprimée');
      charger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const ouvrirHistorique = async (client) => {
    setHistoriqueLoading(true);
    setHistorique({ client, tickets: [] });
    try {
      const reponse = await clientStudioAPI.getHistorique(client.id);
      setHistorique(reponse.data);
    } catch (error) {
      toast.error("Impossible de charger l'historique");
      setHistorique(null);
    } finally {
      setHistoriqueLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="1.5rem 2.5rem">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Clients</Typography>
          <Typography variant="body2" color="text.secondary">
            Le fichier du studio — visites et dépenses cumulées
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => ouvrirDialog()}>
          Nouveau client
        </Button>
      </Box>

      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Fiches</Typography>
            <Typography variant="h4" fontWeight="bold">{clients.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Visites cumulées</Typography>
            <Typography variant="h4" fontWeight="bold">{totalVisites}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Dépensé au total</Typography>
            <Typography variant="h4" fontWeight="bold" color="success.main">
              {htg(totalDepense)}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Box mb={2}>
        <TextField
          size="small"
          placeholder="Chercher un nom, un téléphone, un CIN/NIF"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          sx={{ maxWidth: 380 }}
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
          }}
        />
        {recherche && (
          <Typography variant="caption" color="text.secondary" ml={1}>
            {affiches.length} sur {clients.length}
          </Typography>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Téléphone</TableCell>
              <TableCell>CIN / NIF</TableCell>
              <TableCell align="center">Visites</TableCell>
              <TableCell align="right">Total dépensé</TableCell>
              <TableCell>Dernière visite</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {affiches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <People sx={{ fontSize: 48, color: 'text.disabled' }} />
                  <Typography color="text.secondary" mt={1}>
                    {clients.length === 0
                      ? 'Aucune fiche — elles se créent aussi depuis la caisse'
                      : 'Aucune fiche ne correspond'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : affiches.map((client) => (
              <TableRow key={client.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: 'info.light' }}>
                      {(client.nom || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography fontWeight={600}>{client.nom}</Typography>
                  </Box>
                </TableCell>
                <TableCell>{client.telephone || '—'}</TableCell>
                <TableCell>{client.cin_nif || '—'}</TableCell>
                <TableCell align="center">
                  <Chip size="small" label={client.nombre_visites} />
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight={600} color="success.main">
                    {htg(client.total_depense)}
                  </Typography>
                </TableCell>
                <TableCell>{dateCourte(client.derniere_visite)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Voir ses passages">
                    <IconButton size="small" onClick={() => ouvrirHistorique(client)}>
                      <History />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Modifier">
                    <IconButton size="small" onClick={() => ouvrirDialog(client)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Supprimer">
                    <IconButton size="small" color="error" onClick={() => supprimer(client)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Formulaire */}
      <Dialog open={dialogOuvert} onClose={() => setDialogOuvert(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{enEdition ? 'Modifier la fiche' : 'Nouveau client'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Nom complet"
              value={formulaire.nom}
              onChange={(e) => setFormulaire({ ...formulaire, nom: e.target.value })}
              fullWidth
              autoFocus
              required
            />
            <Box display="flex" gap={2}>
              <TextField
                label="Téléphone"
                value={formulaire.telephone}
                onChange={(e) => setFormulaire({ ...formulaire, telephone: e.target.value })}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Phone fontSize="small" /></InputAdornment>,
                }}
              />
              <TextField
                label="CIN / NIF"
                value={formulaire.cin_nif}
                onChange={(e) => setFormulaire({ ...formulaire, cin_nif: e.target.value })}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start"><BadgeIcon fontSize="small" /></InputAdornment>,
                }}
              />
            </Box>
            <TextField
              label="Notes"
              value={formulaire.notes}
              onChange={(e) => setFormulaire({ ...formulaire, notes: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder="Préférences, allergies, remarques..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOuvert(false)}>Annuler</Button>
          <Button variant="contained" onClick={enregistrer}>
            {enEdition ? 'Enregistrer' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Historique */}
      <Dialog
        open={Boolean(historique)}
        onClose={() => setHistorique(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {historique?.client?.nom}
          <Typography variant="body2" color="text.secondary">
            {historique?.client?.nombre_visites} visite
            {historique?.client?.nombre_visites > 1 ? 's' : ''} ·{' '}
            {htg(historique?.client?.total_depense)} dépensés
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {historiqueLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : historique?.tickets?.length ? (
            <List disablePadding>
              {historique.tickets.map((ticket) => (
                <React.Fragment key={ticket.id}>
                  <ListItem
                    disableGutters
                    secondaryAction={
                      <Typography fontWeight={600}>{htg(ticket.montant_total)}</Typography>
                    }
                  >
                    <ListItemText
                      primary={`${ticket.numero} — ${dateCourte(ticket.date_paiement)}`}
                      secondary={
                        ticket.lignes
                          ?.map((ligne) => `${ligne.nom_item} (${ligne.prestataire_nom})`)
                          .join(' · ') || 'Aucun détail'
                      }
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary" align="center" py={4}>
              Aucun passage enregistré
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistorique(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Clients;
